import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import Container from "../Container";
import ProductCard from "../ProductCard";
import { Product } from "../../types";

interface FeaturedProductsSectionProps {
  products: Product[];
  store_settings: any;
  title?: string;
  category?: { id: number; name: string; slug: string };
}

const FeaturedProductsSection = ({
  products,
  store_settings,
  title,
  category,
}: FeaturedProductsSectionProps) => {
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section className="py-5 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-brand-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-brand-secondary/5 rounded-full blur-3xl" />

      <Container className="relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-0">
            {category ? (
              <>
                Productos Destacados de{" "}
                <span className="relative inline-block">
                  {category.name}
                  <div className="absolute -bottom-1 left-0 w-full h-1 bg-brand-gradient"></div>
                </span>
              </>
            ) : (
              title || (
                <>
                  Lo <span className="text-brand-gradient">Mejor</span> de
                  Nuestra Selección
                </>
              )
            )}
          </h2>

          <a
            href={category ? `/products/${category.slug}` : "/products"}
            className="inline-flex items-center gap-3 px-6 py-3 bg-brand-gradient text-black font-bold rounded-full hover:shadow-brand-lg transition-all duration-300 hover:scale-105 text-base"
          >
            {category
              ? `Ver todo de ${category.name}`
              : "Ver todos los productos"}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>

        {products.length >= 4 ? (
          <div className="relative">
            <Carousel
              opts={{
                align: "start",
                loop: true,
                skipSnaps: false,
                dragFree: false,
                slidesToScroll: 1,
              }}
              plugins={[
                ...(products.length >= 4
                  ? [
                      Autoplay({
                        delay: 4000,
                        stopOnInteraction: true,
                      }),
                    ]
                  : []),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-4 my-10">
                {products.map((product, index) => (
                  <CarouselItem
                    key={`${product.id}-${index}`}
                    className="pl-4 basis-1/2 md:basis-1/4"
                    style={{
                      animationDelay: `${index * 150}ms`,
                      animation: "fadeInUp 0.8s ease-out both",
                      margin: "20px 0",
                    }}
                  >
                    <div className="h-full ">
                      <ProductCard
                        product={product}
                        className="h-full"
                        store_settings={store_settings}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Custom navigation buttons */}
              <CarouselPrevious
                className="left-2 md:left-0 top-1/2 -translate-y-1/2 bg-white/30 dark:bg-gray-800/30 hover:bg-white/60 dark:hover:bg-gray-700/60 border-0 shadow-lg w-12 h-12 hover:scale-110 transition-all duration-300 z-10"
                aria-label="Productos destacados anteriores"
              />
              <CarouselNext
                className="right-2 md:right-0 top-1/2 -translate-y-1/2 bg-white/30 dark:bg-gray-800/30 hover:bg-white/60 dark:hover:bg-gray-700/60 border-0 shadow-lg w-12 h-12 hover:scale-110 transition-all duration-300 z-10"
                aria-label="Productos destacados siguientes"
              />
            </Carousel>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animation: "fadeInUp 0.8s ease-out both",
                  margin: "20px 0",
                }}
              >
                <ProductCard
                  product={product}
                  className="h-full"
                  store_settings={store_settings}
                />
              </div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
};

export default FeaturedProductsSection;
